import React, { useEffect, useState } from "react";
import { Modal, Table, Button, notification } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import { setAttendanceSimple, getAttendanceByModuleSimple } from "../../../../../service/attendance.simple";

const Attendance = ({
  showAttendanceModal,
  closeAttendanceModal,
  singleModuleName,
  singleModuleId,
  singleCourseId,     // unused in new schema
  singleBatchId,
  singleSettingId,    // unused in new schema
  studentsEnrolledInModule,
  trainerId,
}) => {
  const [rows, setRows] = useState(studentsEnrolledInModule || []);
  const [pending, setPending] = useState({}); // { [studentId]: 'present' | 'absent' }

  // 1) Base student list
  useEffect(() => {
    setRows(studentsEnrolledInModule || []);
  }, [studentsEnrolledInModule]);

  // 2) Hydrate marks from Firestore when modal opens or ids change
  useEffect(() => {
    let alive = true;
    (async () => {
      if (!showAttendanceModal || !trainerId || !singleBatchId || !singleModuleId) return;
      try {
        const marks = await getAttendanceByModuleSimple({
          trainerId,
          batchId: singleBatchId,
          moduleId: singleModuleId,
        });
        if (!alive) return;
        const merged = (studentsEnrolledInModule || []).map((s) =>
          marks[s.studentId]
            ? { ...s, attendanceStatus: marks[s.studentId].status, isAttendanceMarked: true }
            : s
        );
        setRows(merged);
      } catch (e) {
        console.error("Hydrate attendance failed:", e);
      }
    })();
    return () => { alive = false; };
  }, [showAttendanceModal, trainerId, singleBatchId, singleModuleId, studentsEnrolledInModule]);

  async function handleMarkAttendance(studentId, mark) {
    try {
      setPending((p) => ({ ...p, [studentId]: mark }));

      await setAttendanceSimple({
        trainerId,
        batchId: singleBatchId,
        moduleId: singleModuleId,
        studentId,
        status: mark, // "present" | "absent"
      });

      // Optimistic UI
      setRows((prev) =>
        prev.map((r) =>
          r.studentId === studentId
            ? { ...r, isAttendanceMarked: true, attendanceStatus: mark }
            : r
        )
      );

      notification.success({ message: "Attendance saved." });
    } catch (e) {
      console.error(e);
      notification.error({ message: "Failed to save attendance." });
    } finally {
      setPending((p) => {
        const { [studentId]: _x, ...rest } = p;
        return rest;
      });
    }
  }

  const columns = [
    {
      title: "Student ID",
      dataIndex: "studentId",
      key: "studentId",
      render: (text) => <span>{text}</span>,
    },
    {
      title: "Student Name",
      dataIndex: "studentName",
      key: "studentName",
      render: (text, r) => (
        <span>
          {text ||
            r.fullname ||
            r.fullName ||
            r.name ||
            r.displayName ||
            r.email ||
            "N/A"}
        </span>
      ),
    },
    {
      title: "Current",
      key: "current",
      render: (_, record) => {
        const s = record.attendanceStatus;
        if (s === "present") return <span className="text-green-500 font-medium">Present</span>;
        if (s === "absent") return <span className="text-red-500 font-medium">Absent</span>;
        return <span className="text-gray-400">Not marked</span>;
      },
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => {
        const isSaving = !!pending[record.studentId];
        const savingWhich = pending[record.studentId];
        const current = record.attendanceStatus;

        return (
          <div className="flex gap-8 items-center">
            <div className="flex gap-8">
              <Button
                type={current === "present" ? "primary" : "default"}
                onClick={() => handleMarkAttendance(record.studentId, "present")}
                disabled={isSaving}
                loading={savingWhich === "present"}
              >
                Present
              </Button>
              <Button
                type={current === "absent" ? "primary" : "default"}
                danger={current === "absent"}
                onClick={() => handleMarkAttendance(record.studentId, "absent")}
                disabled={isSaving}
                loading={savingWhich === "absent"}
              >
                Absent
              </Button>
            </div>
            {isSaving && <span className="text-xs text-gray-400">Saving…</span>}
          </div>
        );
      },
    },
  ];

  return (
    <Modal
      open={showAttendanceModal}
      onCancel={closeAttendanceModal}
      footer={null}
      closeIcon={<CloseOutlined className="text-gray-100" />}
      width={900}
    >
      <div className="p-4">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h2 className="text-2xl font-bold text-white">Attendance</h2>
            <p className="text-sm text-gray-400">
              Module Name: {singleModuleName || "N/A"}
            </p>
          </div>
        </div>
        <Table
          columns={columns}
          dataSource={rows}
          rowKey="studentId"
          pagination={false}
        />
      </div>
    </Modal>
  );
};

export default Attendance;
